import React, { useRef, useEffect, useState } from "react";
import {
  XIcon,
  CheckIcon,
  Edit2Icon,
  RotateCcwIcon,
  PlusIcon,
  MinusIcon,
  FlipHorizontalIcon,
  FlipVerticalIcon,
  CameraIcon,
  ImageIcon,
} from "./icons/FeatherIcons";
import ReactCrop, { type Crop, type PixelCrop } from "react-image-crop";
import "react-image-crop/dist/ReactCrop.css";
import {
  ref,
  uploadBytesResumable,
  getDownloadURL,
  deleteObject,
} from "firebase/storage";
import { useAuth } from "../contexts/AuthContext";
import { usePageTracking } from "@/hooks/usePageTracking";
import { crashlytics } from "@/services/firebase";
import { storage } from "@/services/firebase";
import { CameraService } from "../services/cameraService";
import { Capacitor } from "@capacitor/core";
import { fallbackImage } from "@/utils/getCachedImage";

interface PhotoSelectionModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSelect: (imageUrl: string) => void;
  currentPhoto?: string;
  allowMultiple?: boolean;
}

interface PreviewFile {
  file: File;
  previewUrl: string;
  id: string;
}

const FILTERS = [
  { name: "Normal", value: "none" },
  { name: "B&W", value: "grayscale(100%)" },
  { name: "Sepia", value: "sepia(100%)" },
  { name: "Warm", value: "sepia(50%) saturate(150%)" },
  { name: "Cool", value: "hue-rotate(180deg) saturate(80%)" },
];

const handleImageError = (e: React.SyntheticEvent<HTMLImageElement, Event>) => {
  if (e.currentTarget.src !== fallbackImage) {
    e.currentTarget.src = fallbackImage;
  }
};

const PhotoSelectionModal: React.FC<PhotoSelectionModalProps> = ({
  isOpen,
  onClose,
  onSelect,
  currentPhoto,
  allowMultiple = false,
}) => {
  usePageTracking("PhotoSelection", "PhotoSelectionModal");
  const modalRef = useRef<HTMLDivElement>(null);
  const { currentUser } = useAuth();
  const [selectedFiles, setSelectedFiles] = useState<PreviewFile[]>([]);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState<Record<string, number>>(
    {},
  );
  const imageRef = useRef<HTMLImageElement>(null);

  // Editing state
  const [imageToEdit, setImageToEdit] = useState<string | null>(null);
  const [originalImage, setOriginalImage] = useState<string | null>(null);
  const [crop, setCrop] = useState<Crop>();
  const [completedCrop, setCompletedCrop] = useState<PixelCrop>();
  const [activeFilter, setActiveFilter] = useState("none");
  const [zoom, setZoom] = useState(1);

  const isNative = Capacitor.isNativePlatform();

  const createPreviewUrl = async (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => {
        if (typeof reader.result === "string") {
          resolve(reader.result);
        } else {
          reject(new Error("Failed to create preview"));
        }
      };
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  };

  const handleFileSelect = async (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const files = Array.from(event.target.files || []).filter(
      (file): file is File => file instanceof File,
    );
    const imageFiles = files.filter((file) => file.type.startsWith("image/"));

    // If single selection (profile pic), open editor
    if (!allowMultiple && imageFiles.length > 0) {
      try {
        const previewUrl = await createPreviewUrl(imageFiles[0]);
        setImageToEdit(previewUrl);
        setOriginalImage(previewUrl);
        setCrop({ unit: "%", width: 90, height: 90, x: 5, y: 5 }); // Default crop
        setActiveFilter("none");
        return;
      } catch (error) {
        crashlytics.logError(
          "Error preparing image for edit",
          "PhotoSelectionModal.tsx",
          error,
        );
      }
    }

    const newFiles: PreviewFile[] = [];
    for (const file of imageFiles) {
      try {
        const previewUrl = await createPreviewUrl(file);
        newFiles.push({
          file,
          previewUrl,
          id: crypto.randomUUID(),
        });
      } catch (error) {
        crashlytics.logError(
          `Error creating preview for file ${file?.name}. Error: ${JSON.stringify(error?.message)}`,
          "PhotoSelectionModal.tsx",
          error,
        );
        // Silently fail for this file
      }
    }

    if (allowMultiple) {
      setSelectedFiles((prev) => [...prev, ...newFiles].slice(0, 5)); // Limit to 5 photos
    } else {
      setSelectedFiles(newFiles.slice(-1));
    }
  };

  const handleUpload = async () => {
    if (selectedFiles.length === 0) return;
    setIsUploading(true);
    setUploadProgress({});

    try {
      // Support pre-auth usage (e.g. Sign Up page): return local data URL without uploading
      if (!currentUser) {
        if (!allowMultiple) {
          const firstFile = selectedFiles[0];
          if (firstFile) {
            const imageUrl = firstFile.previewUrl.startsWith("blob:")
              ? await createPreviewUrl(firstFile.file)
              : firstFile.previewUrl;
            onSelect(imageUrl);
            onClose();
          }
        }
        return;
      }

      const uploadedUrls: string[] = [];

      if (!storage) {
        throw new Error("Storage is not initialized");
      }

      for (const { file, id } of selectedFiles) {
        const timestamp = Date.now();
        const uniqueId = crypto.randomUUID();
        const isStory = !currentPhoto;
        const fileName = isStory
          ? `story-photos/${currentUser.id}/${timestamp}-${uniqueId}.jpg`
          : `profile-photos/${currentUser.id}/profile-picture.jpg`;

        const canvas = document.createElement("canvas");
        const image = new Image();

        await new Promise<void>((resolve, reject) => {
          image.onload = () => {
            let targetWidth = 400;
            let targetHeight = 400;
            let width = image.naturalWidth;
            let height = image.naturalHeight;
            const aspectRatio = width / height;

            if (aspectRatio > 1) {
              width = targetWidth;
              height = width / aspectRatio;
            } else {
              height = targetHeight;
              width = height * aspectRatio;
            }

            canvas.width = targetWidth;
            canvas.height = targetHeight;
            const ctx = canvas.getContext("2d");
            if (!ctx) {
              reject(new Error("No 2d context"));
              return;
            }

            ctx.fillStyle = "#FFFFFF";
            ctx.fillRect(0, 0, canvas.width, canvas.height);
            const x = (targetWidth - width) / 2;
            const y = (targetHeight - height) / 2;
            ctx.drawImage(
              image,
              0,
              0,
              image.naturalWidth,
              image.naturalHeight,
              x,
              y,
              width,
              height,
            );
            resolve();
          };
          image.onerror = reject;
          image.src = URL.createObjectURL(file);
        });

        const blob = await new Promise<Blob>((resolve, reject) => {
          canvas.toBlob(
            (b) => {
              if (!b) reject(new Error("Failed to create blob"));
              else resolve(b);
            },
            "image/jpeg",
            1.0,
          );
        });

        const storageRef = ref(storage, fileName);
        const metadata = {
          contentType: "image/jpeg",
          customMetadata: {
            uploadedBy: currentUser.id,
            timestamp: timestamp.toString(),
          },
          cacheControl: "public, max-age=31536000",
        };

        const uploadTask = uploadBytesResumable(storageRef, blob, metadata);

        await new Promise<void>((resolve, reject) => {
          uploadTask.on(
            "state_changed",
            (snapshot) => {
              const progress =
                (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
              setUploadProgress((prev) => ({ ...prev, [id]: progress }));
            },
            reject,
            resolve,
          );
        });

        const downloadUrl = await getDownloadURL(storageRef);
        uploadedUrls.push(downloadUrl);
      }

      if (allowMultiple) {
        // Upload all photos in sequence
        for (const url of uploadedUrls) {
          await onSelect(url);
        }
      } else {
        // For single photo upload (profile picture)
        onSelect(uploadedUrls[0]);
      }
      onClose();
    } catch (error) {
      crashlytics.logError(
        `Error Uploading Photos. Error: ${JSON.stringify(error?.message)}`,
        "PhotoSelectionModal.tsx",
        error,
      );
    } finally {
      setIsUploading(false);
    }

    // No need for additional code here, everything is handled in the handleUpload function above
  };

  const handleEnhancedPhotoSelect = async (dataUrl: string) => {
    // If single selection (profile pic), open editor
    if (!allowMultiple) {
      setImageToEdit(dataUrl);
      setOriginalImage(dataUrl);
      setCrop({ unit: "%", width: 90, height: 90, x: 5, y: 5 });
      setActiveFilter("none");
      return;
    }

    try {
      // Convert data URL to blob
      const response = await fetch(dataUrl);
      const blob = await response.blob();

      // Create a file from the blob
      const file = new File([blob], `photo-${Date.now()}.jpg`, {
        type: "image/jpeg",
      });

      // Create preview
      const previewFile: PreviewFile = {
        file,
        previewUrl: dataUrl,
        id: crypto.randomUUID(),
      };

      if (allowMultiple) {
        setSelectedFiles((prev) => [...prev, previewFile].slice(0, 5));
      } else {
        setSelectedFiles([previewFile]);
      }
    } catch (error) {
      // Silently handle error - user will see no photo selected
    }
  };

  const handleCameraCapture = async () => {
    try {
      if (isNative) {
        const granted = await CameraService.checkAndRequestCameraPermissions();
        if (!granted) return;
      }

      const result = await CameraService.takePhoto({
        allowCamera: true,
        allowGallery: true,
        quality: 90,
        width: allowMultiple ? 800 : 600,
        height: allowMultiple ? 800 : 600,
      });

      if (result.success && result.imageUrl) {
        handleEnhancedPhotoSelect(result.imageUrl);
      }
    } catch (error) {
      crashlytics.logError(
        "Error capturing photo from camera",
        "PhotoSelectionModal.tsx",
        error,
      );
    }
  };

  const handleGallerySelect = async () => {
    try {
      if (isNative) {
        const granted = await CameraService.checkAndRequestPhotosPermissions();
        if (!granted) return;

        const result = await CameraService.selectFromGallery({
          quality: 90,
          width: allowMultiple ? 800 : 600,
          height: allowMultiple ? 800 : 600,
        });
        if (result.success && result.imageUrl) {
          handleEnhancedPhotoSelect(result.imageUrl);
        }
      } else {
        const result = await CameraService.selectPhotoWithOptions({
          quality: 90,
          width: allowMultiple ? 800 : 600,
          height: allowMultiple ? 800 : 600,
        });
        if (result.success && result.imageUrl) {
          handleEnhancedPhotoSelect(result.imageUrl);
        }
      }
    } catch (error) {
      crashlytics.logError(
        "Error selecting photo from gallery",
        "PhotoSelectionModal.tsx",
        error,
      );
    }
  };

  const handleSaveEdit = async () => {
    if (!imageToEdit || !completedCrop || !imageRef.current) return;

    const image = imageRef.current;
    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d");

    if (!ctx) return;

    const scaleX = image.naturalWidth / image.width;
    const scaleY = image.naturalHeight / image.height;
    const pixelRatio = window.devicePixelRatio;

    canvas.width = completedCrop.width * scaleX * pixelRatio;
    canvas.height = completedCrop.height * scaleY * pixelRatio;

    ctx.scale(pixelRatio, pixelRatio);
    ctx.imageSmoothingQuality = "high";

    // Apply filter
    ctx.filter = activeFilter;

    const cropX = completedCrop.x * scaleX;
    const cropY = completedCrop.y * scaleY;
    const cropWidth = completedCrop.width * scaleX;
    const cropHeight = completedCrop.height * scaleY;

    ctx.drawImage(
      image,
      cropX,
      cropY,
      cropWidth,
      cropHeight,
      0,
      0,
      completedCrop.width * scaleX,
      completedCrop.height * scaleY,
    );

    canvas.toBlob(
      (blob) => {
        if (!blob) return;
        const file = new File([blob], "profile-edited.jpg", {
          type: "image/jpeg",
        });
        const previewUrl = URL.createObjectURL(blob);

        setSelectedFiles([
          {
            file,
            previewUrl,
            id: crypto.randomUUID(),
          },
        ]);
        setImageToEdit(null);
      },
      "image/jpeg",
      0.95,
    );
  };

  const handleRotate = async () => {
    if (!imageToEdit) return;

    const image = new Image();
    image.src = imageToEdit;
    await new Promise((resolve) => {
      image.onload = resolve;
    });

    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    // Rotate 90 degrees clockwise
    canvas.width = image.height;
    canvas.height = image.width;

    ctx.translate(canvas.width / 2, canvas.height / 2);
    ctx.rotate((90 * Math.PI) / 180);
    ctx.drawImage(image, -image.width / 2, -image.height / 2);

    const newUrl = canvas.toDataURL("image/jpeg");
    setImageToEdit(newUrl);
    setCrop({ unit: "%", width: 90, height: 90, x: 5, y: 5 });
  };

  const handleFlipHorizontal = async () => {
    if (!imageToEdit) return;

    const image = new Image();
    image.src = imageToEdit;
    await new Promise((resolve) => {
      image.onload = resolve;
    });

    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    canvas.width = image.width;
    canvas.height = image.height;

    ctx.translate(image.width, 0);
    ctx.scale(-1, 1);
    ctx.drawImage(image, 0, 0);

    const newUrl = canvas.toDataURL("image/jpeg");
    setImageToEdit(newUrl);
  };

  const handleFlipVertical = async () => {
    if (!imageToEdit) return;

    const image = new Image();
    image.src = imageToEdit;
    await new Promise((resolve) => {
      image.onload = resolve;
    });

    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    canvas.width = image.width;
    canvas.height = image.height;

    ctx.translate(0, image.height);
    ctx.scale(1, -1);
    ctx.drawImage(image, 0, 0);

    const newUrl = canvas.toDataURL("image/jpeg");
    setImageToEdit(newUrl);
  };

  const handleReset = () => {
    if (originalImage) {
      setImageToEdit(originalImage);
      setActiveFilter("none");
      setCrop({ unit: "%", width: 90, height: 90, x: 5, y: 5 });
      setZoom(1);
    }
  };

  useEffect(() => {
    if (!isOpen) {
      setSelectedFiles([]);
      setIsUploading(false);
      setUploadProgress({});
      setImageToEdit(null);
      setOriginalImage(null);
      setActiveFilter("none");
      setZoom(1);
    }
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    const modal = modalRef.current;
    if (!modal) return;

    const focusableElements = modal.querySelectorAll<HTMLElement>(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
    );
    const firstElement = focusableElements[0];
    const lastElement = focusableElements[focusableElements.length - 1];

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        onClose();
        return;
      }
      if (e.key !== "Tab") return;

      if (e.shiftKey) {
        if (document.activeElement === firstElement) {
          lastElement.focus();
          e.preventDefault();
        }
      } else {
        if (document.activeElement === lastElement) {
          firstElement.focus();
          e.preventDefault();
        }
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    firstElement?.focus();

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 bg-black/70 flex items-center justify-center z-[200]"
      onClick={onClose}
    >
      <div
        ref={modalRef}
        className="bg-white rounded-2xl w-full max-w-lg m-4 p-6 flex flex-col max-h-[90vh]"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="upload-photo-title"
      >
        <div className="flex justify-between items-center mb-6">
          <h2
            id="upload-photo-title"
            className="text-xl font-bold text-gray-900"
          >
            {imageToEdit ? "Edit Photo" : "Upload Photo"}
          </h2>
          <button
            onClick={onClose}
            aria-label="Close modal"
            className="text-gray-500 hover:text-gray-900"
          >
            <XIcon className="w-6 h-6" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto">
          {imageToEdit ? (
            <div className="flex flex-col h-full">
              <div className="flex-1 bg-gray-100 rounded-lg overflow-hidden flex items-center justify-center mb-4">
                <div
                  style={{
                    transform: `scale(${zoom})`,
                    transformOrigin: "center",
                    transition: "transform 0.1s ease-out",
                  }}
                >
                  <ReactCrop
                    crop={crop}
                    onChange={(_, percentCrop) => setCrop(percentCrop)}
                    onComplete={(c) => setCompletedCrop(c)}
                    aspect={allowMultiple ? undefined : 1}
                    circularCrop={!allowMultiple}
                    keepSelection={!allowMultiple}
                    minWidth={50}
                    minHeight={50}
                    ruleOfThirds={true}
                    className="max-h-[50vh]"
                  >
                    <img
                      ref={imageRef}
                      src={imageToEdit}
                      alt="Edit"
                      style={{
                        filter: activeFilter,
                        maxHeight: "50vh",
                        objectFit: "contain",
                      }}
                      onLoad={(e) => {
                        // Set initial crop to center
                        const { width, height } = e.currentTarget;
                        const size = Math.min(width, height) * 0.9;
                        const x = (width - size) / 2;
                        const y = (height - size) / 2;
                        setCrop({
                          unit: "px",
                          width: size,
                          height: size,
                          x,
                          y,
                        });
                      }}
                    />
                  </ReactCrop>
                </div>
              </div>

              <div className="mb-4">
                <div className="flex justify-between items-center mb-2">
                  <p className="text-sm font-medium text-gray-700">
                    Edit Image
                  </p>
                  <div className="flex gap-2">
                    <button
                      onClick={handleRotate}
                      className="p-1.5 text-gray-600 hover:text-primary hover:bg-gray-100 rounded-full transition-colors"
                      title="Rotate 90°"
                    >
                      <RotateCcwIcon className="w-4 h-4 transform scale-x-[-1]" />
                    </button>
                    <button
                      onClick={handleFlipHorizontal}
                      className="p-1.5 text-gray-600 hover:text-primary hover:bg-gray-100 rounded-full transition-colors"
                      title="Flip Horizontal"
                    >
                      <FlipHorizontalIcon className="w-4 h-4" />
                    </button>
                    <button
                      onClick={handleFlipVertical}
                      className="p-1.5 text-gray-600 hover:text-primary hover:bg-gray-100 rounded-full transition-colors"
                      title="Flip Vertical"
                    >
                      <FlipVerticalIcon className="w-4 h-4" />
                    </button>
                    <button
                      onClick={handleReset}
                      className="text-xs text-red-500 hover:text-red-600 font-medium px-2 py-1 rounded hover:bg-red-50 transition-colors"
                    >
                      Reset
                    </button>
                  </div>
                </div>

                <div className="flex items-center gap-3 mb-3 px-1">
                  <span className="text-xs font-medium text-gray-500 w-8">
                    Zoom:
                  </span>
                  <button
                    onClick={() => setZoom((z) => Math.max(1, z - 0.1))}
                    className="text-gray-500 hover:text-primary transition-colors"
                  >
                    <MinusIcon className="w-4 h-4" />
                  </button>
                  <input
                    type="range"
                    min="1"
                    max="3"
                    step="0.1"
                    value={zoom}
                    onChange={(e) => setZoom(parseFloat(e.target.value))}
                    className="flex-1 h-1.5 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-primary"
                  />
                  <button
                    onClick={() => setZoom((z) => Math.min(3, z + 0.1))}
                    className="text-gray-500 hover:text-primary transition-colors"
                  >
                    <PlusIcon className="w-4 h-4" />
                  </button>
                  <span className="w-8"></span>
                </div>

                <div className="flex gap-2 overflow-x-auto pb-2">
                  {FILTERS.map((f) => (
                    <button
                      key={f.name}
                      onClick={() => setActiveFilter(f.value)}
                      className={`px-3 py-1.5 rounded-full text-xs font-medium whitespace-nowrap transition-colors ${
                        activeFilter === f.value
                          ? "bg-primary text-white"
                          : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                      }`}
                    >
                      {f.name}
                    </button>
                  ))}
                </div>
              </div>

              <div className="flex justify-end space-x-3">
                <button
                  onClick={() => setImageToEdit(null)}
                  className="px-4 py-2 text-gray-600 hover:text-gray-800 font-medium"
                >
                  Back
                </button>
                <button
                  onClick={handleSaveEdit}
                  className="px-6 py-2 bg-primary text-white rounded-lg font-medium hover:bg-primary/90"
                >
                  Done
                </button>
              </div>
            </div>
          ) : (
            <>
              {currentPhoto && selectedFiles.length === 0 && (
                <div className="mb-6">
                  <p className="text-gray-600 mb-2">Current Profile Picture:</p>
                  <img
                    src={currentPhoto}
                    alt="Current profile"
                    className="w-32 h-32 rounded-full object-cover mx-auto mb-4"
                    onError={handleImageError}
                  />
                </div>
              )}
              {selectedFiles.length === 0 ? (
                <div className="border-2 border-dashed border-gray-300 rounded-lg p-8">
                  <div className="flex flex-col items-center text-center">
                    <svg
                      className="w-16 h-16 text-gray-400 mb-4"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                      />
                    </svg>
                    <p className="text-gray-600 mb-4">
                      {allowMultiple
                        ? "Select up to 5 photos"
                        : "Select a new profile picture"}
                    </p>
                    <div className="flex flex-col gap-3 w-full max-w-xs mx-auto">
                      <button
                        onClick={handleCameraCapture}
                        className="flex items-center justify-center gap-2 bg-blue-500 hover:bg-blue-600 text-white px-6 py-3 rounded-lg transition-colors text-base font-medium w-full"
                      >
                        <CameraIcon className="w-5 h-5" />
                        Take Photo
                      </button>
                      <button
                        onClick={handleGallerySelect}
                        className="flex items-center justify-center gap-2 bg-gray-100 hover:bg-gray-200 text-gray-800 px-6 py-3 rounded-lg transition-colors text-base font-medium w-full"
                      >
                        <ImageIcon className="w-5 h-5" />
                        Choose from Gallery
                      </button>
                    </div>
                    <p className="text-sm text-gray-500 mt-2">
                      Supported formats: JPG, PNG
                    </p>
                  </div>
                </div>
              ) : (
                <div className="space-y-6">
                  <div className="relative rounded-lg overflow-hidden bg-gray-50 grid grid-cols-1 md:grid-cols-2 gap-4">
                    {selectedFiles.map((file) => (
                      <div key={file.id} className="aspect-square relative">
                        <img
                          src={file.previewUrl}
                          alt="Preview"
                          className="w-full h-full object-contain rounded-lg"
                          style={{
                            maxWidth: "400px",
                            maxHeight: "400px",
                            margin: "0 auto",
                            display: "block",
                          }}
                        />
                        {uploadProgress[file.id] !== undefined && (
                          <div className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center">
                            <div className="bg-white rounded-lg p-2">
                              <div className="text-sm font-medium text-gray-900">
                                {Math.round(uploadProgress[file.id])}%
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                  <div className="flex justify-end space-x-3">
                    <button
                      onClick={() => setSelectedFiles([])}
                      className="px-4 py-2 text-gray-600 hover:text-gray-800 font-medium"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleUpload}
                      disabled={isUploading}
                      className={`px-6 py-2 bg-blue-500 text-white rounded-lg flex items-center space-x-2 font-medium
                    ${isUploading ? "opacity-75 cursor-not-allowed" : "hover:bg-blue-600"}`}
                    >
                      {isUploading ? (
                        <>
                          <svg
                            className="animate-spin -ml-1 mr-2 h-5 w-5 text-white"
                            xmlns="http://www.w3.org/2000/svg"
                            fill="none"
                            viewBox="0 0 24 24"
                          >
                            <circle
                              className="opacity-25"
                              cx="12"
                              cy="12"
                              r="10"
                              stroke="currentColor"
                              strokeWidth="4"
                            ></circle>
                            <path
                              className="opacity-75"
                              fill="currentColor"
                              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                            ></path>
                          </svg>
                          <span>Uploading{allowMultiple ? " Photos" : ""}</span>
                        </>
                      ) : (
                        <>
                          <CheckIcon className="w-5 h-5" />
                          <span>Upload{allowMultiple ? " Photos" : ""}</span>
                        </>
                      )}
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default PhotoSelectionModal;
